"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SliderHandleWidget = exports.SliderHandleIconType = void 0;
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var InteractiveDom_1 = require("../../utils/InteractiveDom");
var Icons_1 = require("../../icons/Icons");
var Declarative_1 = require("../../utils/Declarative");
var Geometry_1 = require("../../utils/Geometry");
var SliderStyles_1 = require("./SliderStyles");
var FocusIndicators_1 = require("../../styles/FocusIndicators");
var FocusIndicatorStyleName = TypeStyle.style({
    $nest: {
        '&.focus-within::after': {
            content: "\"\"",
            pointerEvents: 'none',
            width: 16,
            height: 16,
            display: 'inline-block',
            borderRadius: '50%',
            top: 12,
            left: -8,
            position: 'absolute',
            boxShadow: FocusIndicators_1.DefaultFocusIndicator.boxShadow,
        },
        '&:not(:only-child).focus-within::after': {
            borderTopLeftRadius: '66.66% 50%',
            borderBottomLeftRadius: '66.66% 50%',
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
            left: -12,
            width: 13,
        },
        '&:not(:only-child):last-child.focus-within::after': {
            transform: 'rotate(180deg)',
            transformOrigin: '12px 8px',
        }
    }
});
var SliderHandleIconType;
(function (SliderHandleIconType) {
    SliderHandleIconType["Normal"] = "normal";
    SliderHandleIconType["Left"] = "left";
    SliderHandleIconType["Right"] = "right";
})(SliderHandleIconType = exports.SliderHandleIconType || (exports.SliderHandleIconType = {}));
/**
 * This is just the handle part of the slider.
 *
 * It is interactive, BUT that logic (e.g. dragging) is handled by the container.  This allows some stuff like clicking
 * anywhere on the track to grab and drag the handle, rather than only being able to click on the handle.
 */
var SliderHandleWidget = /** @class */ (function (_super) {
    tslib_1.__extends(SliderHandleWidget, _super);
    function SliderHandleWidget(props) {
        var _this = _super.call(this, props) || this;
        // We capture this ref so that we can 'focus()' it, and it is used indirectly for some hit testing logic in the
        // SliderHandleContainer.
        _this.imgRef = null;
        // We capture this ref so that can give its client bounding box out to the SliderHandleContainer.  This area is
        // effectively the handle's touch target (or mouse target) area.
        _this.interactiveAreaRef = null;
        _this.chooseIconAndStylesForRender = function () {
            var styles = SliderStyles_1.getSliderHandleStyles();
            var iconSet = undefined;
            var interactiveAreaStyle = undefined;
            var imgStyle = undefined;
            switch (_this.props.iconType) {
                case SliderHandleIconType.Left:
                    iconSet = Icons_1.SliderLeftHandleIcon;
                    interactiveAreaStyle = styles.HandleInteractiveAreaLeft;
                    imgStyle = styles.HandleIconLeft;
                    break;
                case SliderHandleIconType.Right:
                    iconSet = Icons_1.SliderRightHandleIcon;
                    interactiveAreaStyle = styles.HandleInteractiveAreaRight;
                    imgStyle = styles.HandleIconRight;
                    break;
                default:
                    iconSet = Icons_1.SliderHandleIcon;
                    interactiveAreaStyle = styles.HandleInteractiveArea;
                    imgStyle = styles.HandleIcon;
                    break;
            }
            if (_this.props.disabled) {
                interactiveAreaStyle = tslib_1.__assign(tslib_1.__assign({}, interactiveAreaStyle), styles.DisabledHandleMixin);
                imgStyle = tslib_1.__assign(tslib_1.__assign({}, imgStyle), styles.DisabledHandleInnerFocusAreaMixin);
            }
            var iconState = {
                active: _this.state.isHovered || _this.props.isActive,
                disabled: _this.props.disabled,
                focus: _this.state.isFocused,
            };
            var iconImageDataUrl = iconSet.icon(iconState);
            return {
                positioningContainerStyle: styles.PositioningContainer,
                pressIndicationBarStyle: styles.PressIndicationBar,
                interactiveAreaStyle: interactiveAreaStyle,
                imgStyle: imgStyle,
                iconImageDataUrl: iconImageDataUrl,
            };
        };
        _this.handleImgRef = function (element) {
            _this.imgRef = element;
        };
        _this.handleInteractiveAreaRef = function (element) {
            _this.interactiveAreaRef = element;
        };
        _this.handleFocus = function (focus) {
            _this.setState({ isFocused: !_this.props.disabled && focus });
        };
        _this.handleHover = function (hover) {
            if (!_this.props.disabled) {
                _this.setState({ isHovered: hover });
            }
        };
        _this.state = { isHovered: false, isFocused: false };
        return _this;
    }
    SliderHandleWidget.prototype.focus = function () {
        if (this.imgRef) {
            this.imgRef.focus();
        }
    };
    SliderHandleWidget.prototype.getBoundingClientRectForIcon = function () {
        if (this.imgRef) {
            var rect = this.imgRef.getBoundingClientRect();
            // The left and right icons have several pixels of transparency between what appears like the edge of the artwork
            // and the actual edge of the image.  Unfortunately we are using the client bounding rect of the icon for some
            // behavior (e.g. see the dragStart method in SliderHandleContainer, which has logic that has to know whether
            // the user (with a mouse) clicked specifically on the icon or not).
            // To make the actual client bounding rect matchup visually with the edge of the artwork, we have to shrink
            // the bounding rect slightly.
            if (this.props.iconType === SliderHandleIconType.Left) {
                return Geometry_1.addClientRectRight(rect, -1 * Icons_1.SliderLeftHandleIcon.artworkOffsetFromRightSide);
            }
            else if (this.props.iconType === SliderHandleIconType.Right) {
                return Geometry_1.addClientRectLeft(rect, Icons_1.SliderRightHandleIcon.artworkOffsetFromLeftSide);
            }
            return rect;
        }
        else {
            throw new Error("Can't determinte bounding rectangle of unmounted handle");
        }
    };
    SliderHandleWidget.prototype.getBoundingClientRectForInteractiveArea = function () {
        if (this.interactiveAreaRef) {
            return this.interactiveAreaRef.getBoundingClientRect();
        }
        else {
            throw new Error("Can't determinte bounding rectangle of unmounted element");
        }
    };
    SliderHandleWidget.prototype.render = function () {
        var dataTestId = Declarative_1.makeTestId(this.props.testId, 'SliderHandle');
        var positionAsPercentage = this.props.position * 100;
        var _a = this.chooseIconAndStylesForRender(), positioningContainerStyle = _a.positioningContainerStyle, pressIndicationBarStyle = _a.pressIndicationBarStyle, interactiveAreaStyle = _a.interactiveAreaStyle, imgStyle = _a.imgStyle, iconImageDataUrl = _a.iconImageDataUrl;
        var renderTouchDragIndicator = this.props.isActive && this.props.isBeingTouched;
        return (React.createElement(InteractiveDom_1.InteractiveDomWrapper, { handleHover: this.handleHover, handleFocus: this.handleFocus },
            React.createElement("span", { className: TypeStyle.classes(FocusIndicatorStyleName, this.state.isFocused && 'focus-within'), "data-tb-test-id": dataTestId, style: tslib_1.__assign(tslib_1.__assign({}, positioningContainerStyle), { left: positionAsPercentage + "%" }) },
                renderTouchDragIndicator &&
                    React.createElement("span", { style: pressIndicationBarStyle, "data-tb-test-id": dataTestId + '-touch-drag-indicator' }),
                React.createElement("span", { style: interactiveAreaStyle, ref: this.handleInteractiveAreaRef }),
                React.createElement("img", tslib_1.__assign({}, this.props.ariaAttributes, { "aria-orientation": 'horizontal', "data-tb-test-id": Declarative_1.makeTestId(this.props.testId, 'SliderHandleIconArea'), onKeyDown: this.props.onKeyDown, onKeyUp: this.props.onKeyUp, ref: this.handleImgRef, role: 'slider', src: iconImageDataUrl, style: imgStyle, tabIndex: this.props.disabled ? undefined : 0 })))));
    };
    return SliderHandleWidget;
}(React.Component));
exports.SliderHandleWidget = SliderHandleWidget;
//# sourceMappingURL=SliderHandleWidget.js.map